import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, Download, ArrowRight, Star } from "lucide-react";
import api, { money } from "@/lib/api";
import Seo from "@/components/Seo";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";

const POLL_MAX = 12;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { clear } = useCart();
  const { user } = useAuth();
  const [state, setState] = useState("polling"); // polling | paid | timeout | error
  const [order, setOrder] = useState(null);
  const cleared = useRef(false);

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setOrder(data); setState("paid");
          if (!cleared.current) { clear(); cleared.current = true; }
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) { setState("error"); return; }
      } catch (e) { /* keep trying */ }
      attempts += 1;
      if (attempts >= POLL_MAX) { setState("timeout"); return; }
      setTimeout(poll, 2000);
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div className="max-w-lg mx-auto px-5 py-28 text-center" data-testid="payment-success-page">
      <Seo title="Order Confirmed" path="/payment/success" noindex />
      {state === "polling" && (
        <>
          <Loader2 className="w-12 h-12 text-primary animate-spin mx-auto mb-6" />
          <h1 className="font-display text-3xl font-light">Confirming your payment…</h1>
          <p className="text-muted-foreground mt-3">Hang tight, this only takes a moment.</p>
        </>
      )}

      {state === "paid" && (
        <>
          <CheckCircle2 className="w-14 h-14 text-primary mx-auto mb-6" />
          <h1 className="font-display text-4xl font-light">Thank you!</h1>
          <p className="text-muted-foreground mt-3">
            Your order <span className="font-mono text-foreground">#{order?.order_id?.slice(0, 8).toUpperCase()}</span> is confirmed.
          </p>
          {order && (
            <div className="border border-border bg-card mt-8 text-left divide-y divide-border">
              {order.items.map((it) => (
                <div key={it.slug} className="flex justify-between px-5 py-3 text-sm">
                  <span>{it.name} <span className="text-muted-foreground font-mono">×{it.quantity}</span></span>
                  <span className="font-mono">{money(it.price * it.quantity)}</span>
                </div>
              ))}
              <div className="flex justify-between px-5 py-3 font-mono font-semibold">
                <span>Total</span><span>{money(order.amount)}</span>
              </div>
            </div>
          )}
          {order?.downloads?.length > 0 && (
            <div className="border border-primary/40 bg-primary/5 mt-6 p-5 text-left" data-testid="success-downloads">
              <p className="font-mono-label text-[11px] text-primary mb-3 flex items-center gap-2">
                <Download className="w-4 h-4" /> Your downloads are ready — no login needed
              </p>
              <div className="space-y-2">
                {order.downloads.map((d) => (
                  <a key={d.slug} href={d.url} target="_blank" rel="noopener noreferrer"
                    data-testid={`success-download-${d.slug}`}
                    className="flex items-center justify-between gap-3 bg-primary text-primary-foreground px-4 py-3 rounded-md hover:brightness-110 transition-[filter] text-sm">
                    <span className="truncate">{d.name}</span>
                    <Download className="w-4 h-4 shrink-0" />
                  </a>
                ))}
              </div>
              <p className="text-[11px] text-muted-foreground mt-3">We've also emailed these links to you.</p>
            </div>
          )}
          {order?.has_digital && !order?.downloads?.length && (
            <p className="text-sm text-muted-foreground mt-6 inline-flex items-center gap-2">
              <Download className="w-4 h-4 text-primary" /> Preparing your downloads… check your email in a moment.
            </p>
          )}
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <Link to={user ? "/account" : "/shop"} data-testid="success-account-btn"
              className="inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground font-mono-label text-xs px-6 py-3 rounded-full hover:brightness-110 transition-[filter]">
              {user ? "View Orders & Downloads" : "Keep shopping"} <ArrowRight className="w-4 h-4" />
            </Link>
            {user && (
              <Link to="/shop" className="inline-flex items-center justify-center border border-border font-mono-label text-xs px-6 py-3 rounded-full hover:border-primary transition-colors">
                Keep shopping
              </Link>
            )}
          </div>
          <p className="text-xs text-muted-foreground mt-6 inline-flex items-center gap-1.5" data-testid="success-review-nudge">
            <Star className="w-3.5 h-3.5 text-primary" />
            Once you've grown, we'd love your review — leave one anytime from <Link to="/account" className="text-primary hover:underline">your account</Link>.
          </p>
        </>
      )}

      {(state === "timeout" || state === "error") && (
        <>
          <XCircle className="w-14 h-14 text-destructive mx-auto mb-6" />
          <h1 className="font-display text-3xl font-light">
            {state === "timeout" ? "Still processing…" : "Something went wrong"}
          </h1>
          <p className="text-muted-foreground mt-3">
            {state === "timeout"
              ? "Your payment is taking longer than expected. Check your account shortly for confirmation."
              : "We couldn't confirm this payment. If you were charged, contact us and we'll sort it out."}
          </p>
          <Link to="/account" className="mt-8 inline-flex font-mono-label text-xs text-primary hover:underline">Go to my account →</Link>
        </>
      )}
    </div>
  );
}
